/** A server-issued ad-account id. No longer a closed union: GRO-39 accounts are UUIDs. */
export type AccountId = string

/** The connection states the server names (GRO-41 design, section 5). */
export const CONNECTION_STATES = [
  'demo',
  'credential_revoked',
  'credential_expired',
  'credential_rejected',
  'access_withdrawn',
  'credential_expiring',
  'connected',
] as const

export type ConnectionState = (typeof CONNECTION_STATES)[number]

/** What the account panel says about one account's connection. */
export interface Connection {
  state: ConnectionState
  /** ISO timestamp of the last successful pull, or `null` before the first. */
  lastSuccessfulPullAt: string | null
  /** ISO timestamp the credential expires at; `null` for a demo account. */
  credentialExpiresAt: string | null
}

/** One of a client's ad accounts, as `GET`/`POST /api/companies` returns it. */
export interface ClientAccount {
  id: AccountId
  name: string
  isDemo: boolean
  /**
   * What tells two same-named accounts apart (GRO-103): `null` for a demo account and for
   * a portfolio Meta names without a name; absent from a backend that predates them.
   */
  currency?: string | null
  portfolioName?: string | null
  /**
   * Absent when the backend predates GRO-41, or names a state this build does not know:
   * the panel then renders no state line rather than guessing one.
   */
  connection?: Connection
}

/**
 * The `currency · portfolio` label that tells same-named accounts apart (GRO-103): the
 * labels present, joined, or `''` for a demo account and for one with neither. Every
 * surface that names an account uses it, so they cannot disagree.
 */
export function accountDetail(account: {
  isDemo?: boolean
  currency?: string | null
  portfolioName?: string | null
}): string {
  if (account.isDemo) return ''
  return [account.currency, account.portfolioName].filter(Boolean).join(' · ')
}
